import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/addons/utils/SkeletonUtils.js";
import type { RaptorRaceState } from "./types.js";
import { raceFinishOrder, raceRandom, raceRunnerProgress } from "./raptor-race-motion.js";

type Runner = { id: string; wrap: THREE.Group; mixer: THREE.AnimationMixer; run: THREE.AnimationAction; idle: THREE.AnimationAction; marker: THREE.Sprite };
let active: { host: HTMLElement; raceId: string; update: (state: RaptorRaceState) => void; dispose: () => void } | undefined;
const startX = -75;
const finishX = 75;
const lanes = [-4, -2.4, -.8, .8, 2.4, 4];

export function disposeRaptorRace3d(): void { active?.dispose(); active = undefined; }

export async function mountRaptorRace3d(host: HTMLElement, initial: RaptorRaceState): Promise<void> {
  if (active?.host === host && active.raceId === initial.race.raceId) { active.update(initial); return; }
  disposeRaptorRace3d();
  const canvas = host.querySelector<HTMLCanvasElement>("canvas");
  if (!canvas || !initial.race.entrants.length) return;
  let state = initial;
  let offset = Date.parse(state.serverNow) - Date.now();
  let disposed = false;
  let frame = 0;
  let renderer: THREE.WebGLRenderer | undefined;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, .1, 350);
  const textures = new Set<THREE.Texture>();
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
  const resize = () => {
    if (!renderer) return;
    renderer.setSize(Math.max(1, host.clientWidth), Math.max(1, host.clientHeight), false);
    camera.aspect = Math.max(1, host.clientWidth) / Math.max(1, host.clientHeight);
    camera.updateProjectionMatrix();
  };
  const observer = new ResizeObserver(resize);
  const dispose = () => {
    disposed = true;
    cancelAnimationFrame(frame);
    observer.disconnect();
    disposeScene(scene, textures);
    renderer?.dispose();
  };
  active = { host, raceId: initial.race.raceId, update: next => { state = next; const measured = Date.parse(next.serverNow) - Date.now(); offset += Math.abs(measured - offset) > 1000 ? measured - offset : (measured - offset) * .2; }, dispose };
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    scene.background = new THREE.Color(0x7f9b87);
    scene.fog = new THREE.Fog(0x829078, 45, 155);
    buildTrack(scene, textures);
    observer.observe(host);
    resize();
    const loaded = await Promise.allSettled([
      new GLTFLoader().loadAsync("/portal/assets/skins/omniraptor/model.glb"),
      new THREE.TextureLoader().loadAsync("/portal/assets/skins/omniraptor/pattern.png"),
      new THREE.TextureLoader().loadAsync("/portal/assets/skins/omniraptor/parts.png")
    ]);
    const model = loaded[0].status === "fulfilled" ? loaded[0].value : undefined;
    const pattern = loaded[1].status === "fulfilled" ? loaded[1].value : undefined;
    const parts = loaded[2].status === "fulfilled" ? loaded[2].value : undefined;
    if (pattern) textures.add(pattern);
    if (parts) textures.add(parts);
    if (disposed || !model || !pattern || !parts) {
      if (model) disposeScene(model.scene, textures);
      else textures.forEach(texture => texture.dispose());
      if (!disposed) throw new Error("Runner assets unavailable");
      return;
    }
    for (const texture of [pattern, parts]) { texture.colorSpace = THREE.NoColorSpace; texture.flipY = false; }
    const runClip = model.animations.find(clip => clip.name === "adult_run");
    const idleClip = model.animations.find(clip => clip.name === "adult_idle");
    if (!runClip || !idleClip) { disposeScene(model.scene, textures); throw new Error("Reviewed runner animations unavailable"); }
    const scaleCanvas = document.createElement("canvas"); scaleCanvas.width = scaleCanvas.height = 192;
    const scaleContext = scaleCanvas.getContext("2d")!;
    scaleContext.fillStyle = "#777"; scaleContext.fillRect(0, 0, 192, 192);
    for (let row = 0; row < 24; row++) for (let column = 0; column < 24; column++) {
      scaleContext.fillStyle = "#aaa"; scaleContext.beginPath();
      scaleContext.ellipse(column * 8 + row % 2 * 4, row * 8, 3.5, 3, 0, 0, Math.PI * 2); scaleContext.fill();
    }
    const scales = new THREE.CanvasTexture(scaleCanvas); scales.wrapS = scales.wrapT = THREE.RepeatWrapping; scales.repeat.set(5, 5); textures.add(scales);
    const runners: Runner[] = state.race.entrants.slice(0, 6).map((entrant, index) => {
      const dino = cloneSkeleton(model.scene);
      const wrap = new THREE.Group();
      wrap.add(dino);
      scene.add(wrap);
      dino.traverse(object => {
        if (!(object instanceof THREE.Mesh)) return;
        object.castShadow = true;
        object.receiveShadow = true;
        object.frustumCulled = false;
        const material = (Array.isArray(object.material) ? object.material[0] : object.material)?.clone();
        if (!material) return;
        object.material = material;
        if (material instanceof THREE.MeshStandardMaterial && material.name === "MI_Omniraptor_Body") { configureBody(material, pattern, parts, entrant.color, index); material.bumpMap = scales; material.bumpScale = .008; }
        if (material instanceof THREE.MeshStandardMaterial && material.name === "MI_Omniraptor_Eye") { material.color.set(0xd1a848); material.roughness = .2; }
      });
      const mixer = new THREE.AnimationMixer(dino);
      const idle = mixer.clipAction(idleClip).play();
      const run = mixer.clipAction(runClip).play();
      run.setEffectiveWeight(0);
      mixer.setTime(0);
      dino.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(dino);
      const scale = 1.65 / Math.max(box.getSize(new THREE.Vector3()).y, .01);
      dino.scale.multiplyScalar(scale);
      dino.position.y -= box.min.y * scale;
      dino.updateMatrixWorld(true);
      const head = dino.getObjectByName("DinoHead");
      const tail = dino.getObjectByName("DinoTail06");
      if (head && tail) {
        const direction = head.getWorldPosition(new THREE.Vector3()).sub(tail.getWorldPosition(new THREE.Vector3()));
        wrap.rotation.y = Math.atan2(direction.z, direction.x);
      }
      wrap.position.set(startX, .04, lanes[index]!);
      const marker = makeLabel(String(index + 1).padStart(2, "0"), entrant.color, textures, 1);
      marker.position.set(startX, 2.5, lanes[index]!);
      marker.scale.set(.65, .65, 1);
      scene.add(marker);
      return { id: entrant.id, wrap, mixer, idle, run, marker };
    });
    const dustGeometry = new THREE.BufferGeometry();
    const dustPositions = new Float32Array(6 * 30 * 3);
    dustGeometry.setAttribute("position", new THREE.BufferAttribute(dustPositions, 3));
    const dustCanvas = document.createElement("canvas"); dustCanvas.width = dustCanvas.height = 32;
    const dustContext = dustCanvas.getContext("2d")!;
    const dustGradient = dustContext.createRadialGradient(16, 16, 0, 16, 16, 16);
    dustGradient.addColorStop(0, "rgba(255,255,255,.7)"); dustGradient.addColorStop(1, "rgba(255,255,255,0)");
    dustContext.fillStyle = dustGradient; dustContext.fillRect(0, 0, 32, 32);
    const dustTexture = new THREE.CanvasTexture(dustCanvas); textures.add(dustTexture);
    const dust = new THREE.Points(dustGeometry, new THREE.PointsMaterial({ map: dustTexture, color: 0xe5c896, size: .3, transparent: true, opacity: .25, depthWrite: false }));
    scene.add(dust);
    host.classList.add("is-ready");
    host.classList.remove("has-error");
    let last = 0;
    let cameraBlend = 0;
    const animate = (now: number) => {
      if (disposed || !renderer) return;
      frame = requestAnimationFrame(animate);
      if (host.closest<HTMLElement>("[data-gambling-panel]")?.hidden || document.hidden || now - last < (reduced.matches ? 100 : 16)) return;
      const delta = Math.min(.1, (now - last) / 1000);
      last = now;
      const elapsed = (Date.now() + offset - Date.parse(state.race.startsAt)) / 1000;
      const duration = (Date.parse(state.race.finishesAt) - Date.parse(state.race.startsAt)) / 1000;
      const progress = state.race.phase === "finished" ? 1 : state.race.phase === "racing" ? THREE.MathUtils.clamp(elapsed / duration, 0, 1) : 0;
      const coastTime = THREE.MathUtils.clamp(elapsed - duration, 0, 4);
      const coast = 10 * (1 - Math.pow(1 - coastTime / 4, 2));
      const runWeight = progress > 0 && !reduced.matches ? THREE.MathUtils.smoothstep(elapsed, 0, .7) * (1 - THREE.MathUtils.smoothstep(coastTime, 1.8, 4)) : 0;
      const running = runWeight > 0;
      const order = raceFinishOrder(state);
      runners.forEach((runner, index) => {
        const position = raceRunnerProgress(progress, index, order.indexOf(runner.id));
        runner.wrap.position.x = startX + position * (finishX - startX) + coast;
        runner.run.setEffectiveWeight(runWeight);
        runner.idle.setEffectiveWeight(1 - runWeight);
        runner.mixer.setTime(reduced.matches ? 0 : (running ? (position * (finishX - startX) + coast) / 4.5 : (Date.now() + offset) / 1000) + index * .237);
        runner.marker.position.x = runner.wrap.position.x;
        runner.marker.visible = !reduced.matches || state.race.phase !== "racing";
        for (let particle = 0; particle < 30; particle++) {
          const t = ((Math.max(0, elapsed) * .7 + particle / 30 + index * .11) % 1);
          const at = (index * 30 + particle) * 3;
          dustPositions[at] = runner.wrap.position.x - .5 - t * 3;
          dustPositions[at + 1] = .1 + Math.sin(t * Math.PI) * .65;
          dustPositions[at + 2] = lanes[index]! + Math.sin(particle * 4.2) * t * .6;
        }
      });
      dust.visible = running;
      dustGeometry.attributes.position!.needsUpdate = true;
      const pack = runners.reduce((sum, runner) => sum + runner.wrap.position.x, 0) / runners.length;
      const narrow = camera.aspect < 1.15;
      const overview = host.dataset.camera === "overview";
      cameraBlend += ((overview ? 1 : 0) - cameraBlend) * (1 - Math.exp(-delta * 5));
      const sweep = reduced.matches ? 0 : Math.sin(progress * Math.PI) * 2;
      if (reduced.matches) {
        camera.position.set(0, 105, 130);
        camera.lookAt(0, 0, 0);
      } else {
        camera.position.set(pack - 5 + sweep, (narrow ? 11 : 8) + cameraBlend * 8, (narrow ? 23 : 16) + cameraBlend * 8);
        camera.lookAt(pack + .8, .9, 0);
      }
      renderer.render(scene, camera);
    };
    frame = requestAnimationFrame(animate);
  } catch (error) {
    if (disposed) return;
    dispose();
    host.classList.add("has-error");
    const message = host.querySelector<HTMLElement>("[data-raptor-3d-loading]");
    if (message) message.textContent = "Track view unavailable. Race timing and results remain live below.";
    console.error("Raptor race presentation unavailable", error);
  }
}

function disposeScene(scene: THREE.Object3D, textures: Set<THREE.Texture>): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  scene.traverse(object => {
    if (!(object instanceof THREE.Mesh || object instanceof THREE.Points || object instanceof THREE.Sprite)) return;
    if ("geometry" in object) geometries.add(object.geometry);
    (Array.isArray(object.material) ? object.material : [object.material]).forEach(material => {
      materials.add(material);
      const map = (material as THREE.MeshStandardMaterial).map;
      if (map) textures.add(map);
    });
  });
  geometries.forEach(value => value.dispose());
  materials.forEach(value => value.dispose());
  textures.forEach(value => value.dispose());
  scene.clear();
}

function makeLabel(text: string, color: string, textures: Set<THREE.Texture>, square = 0): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = square ? 128 : 1024;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#101f18"; ctx.fillRect(0, 0, canvas.width, 128);
  ctx.strokeStyle = color; ctx.lineWidth = 8; ctx.strokeRect(4, 4, canvas.width - 8, 120);
  ctx.fillStyle = square ? "#fff3d7" : color;
  ctx.font = `bold ${square ? 66 : 62}px sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(text, canvas.width / 2, 67);
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; textures.add(texture);
  return new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthTest: true }));
}

function buildTrack(scene: THREE.Scene, textures: Set<THREE.Texture>): void {
  const random = raceRandom("the-nest-jungle-circuit-1");
  scene.add(new THREE.HemisphereLight(0xf9ecd0, 0x394b32, 1.9));
  const sun = new THREE.DirectionalLight(0xffe6b6, 2.2); sun.position.set(-15, 30, 18); sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048); Object.assign(sun.shadow.camera, { left: -88, right: 88, top: 25, bottom: -25, far: 100 }); sun.shadow.bias = -.0005;
  scene.add(sun);
  const dirt = document.createElement("canvas"); dirt.width = dirt.height = 512;
  const ctx = dirt.getContext("2d")!; ctx.fillStyle = "#8e724c"; ctx.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 23000; i++) { const light = 90 + Math.floor(random() * 70); ctx.fillStyle = `rgba(${light + 35},${light + 10},${light - 25},.24)`; ctx.fillRect(random() * 512, random() * 512, 1 + random() * 3, .5 + random() * 2); }
  const texture = new THREE.CanvasTexture(dirt); texture.colorSpace = THREE.SRGBColorSpace; texture.wrapS = texture.wrapT = THREE.RepeatWrapping; texture.repeat.set(28, 4); textures.add(texture);
  const grassCanvas = document.createElement("canvas"); grassCanvas.width = grassCanvas.height = 512;
  const grassContext = grassCanvas.getContext("2d")!;
  grassContext.fillStyle = "#38452b"; grassContext.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 28000; i++) {
    const shade = 20 + Math.floor(random() * 42);
    grassContext.fillStyle = `rgba(${shade + 10},${shade + 22},${shade},.5)`;
    grassContext.fillRect(random() * 512, random() * 512, random() * 3 + 1, random() * 5 + 1);
  }
  const grassTexture = new THREE.CanvasTexture(grassCanvas); grassTexture.colorSpace = THREE.SRGBColorSpace;
  grassTexture.wrapS = grassTexture.wrapT = THREE.RepeatWrapping; grassTexture.repeat.set(45, 18); textures.add(grassTexture);
  const terrain = new THREE.Mesh(new THREE.PlaneGeometry(280, 120), new THREE.MeshStandardMaterial({ map: grassTexture, roughness: 1 })); terrain.rotation.x = -Math.PI / 2; terrain.position.y = -.035; terrain.receiveShadow = true; scene.add(terrain);
  const track = new THREE.Mesh(new THREE.PlaneGeometry(190, 12), new THREE.MeshStandardMaterial({ map: texture, roughness: .96 })); track.rotation.x = -Math.PI / 2; track.receiveShadow = true; scene.add(track);
  const gold = new THREE.MeshStandardMaterial({ color: 0xe8c87b, roughness: .52, metalness: .35 });
  const dark = new THREE.MeshStandardMaterial({ color: 0x293b2c, roughness: .9 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x64523a, roughness: 1 });
  const pale = new THREE.MeshStandardMaterial({ color: 0xf3e4bb, roughness: .9 });
  const box = (x: number, y: number, z: number, w: number, h: number, d: number, material: THREE.Material) => {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material); mesh.position.set(x, y, z); mesh.castShadow = true; mesh.receiveShadow = true; scene.add(mesh); return mesh;
  };
  for (let i = 0; i <= 6; i++) box(0, .009, -4.8 + i * 1.6, 190, .012, .025, pale);
  for (const z of [-6, 6]) {
    for (let x = -94; x <= 94; x += 4) { box(x, .6, z, .16, 1.2, .16, wood); box(x, 1.22, z, .24, .09, .24, gold); }
    box(0, .42, z, 190, .11, .12, wood); box(0, 1.02, z, 190, .11, .12, wood);
  }
  for (const x of [startX - 1, finishX]) {
    box(x, 2, -5.6, .35, 4, .35, dark); box(x, 2, 5.6, .35, 4, .35, dark); box(x, 3.9, -5.6, .5, .2, .5, gold);
    const sign = makeLabel(x === finishX ? "FINISH / THE NEST" : "RAPTOR RUN / START", "#edcc82", textures); sign.position.set(x, 4.6, -6.2); sign.scale.set(6.5, .85, 1); scene.add(sign);
    for (let lane = 0; lane < 12; lane++) for (let row = 0; row < 2; row++) box(x + row * .35, .017, -4.8 + lane * .8, .35, .025, .8, (lane + row) % 2 ? dark : pale);
  }
  const rockMaterials = [0x626b53, 0x7c8067, 0x43563d].map(color => new THREE.MeshStandardMaterial({ color, roughness: 1 }));
  for (let i = 0; i < 32; i++) {
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(.2 + random() * .55, 2), rockMaterials[i % 3]!);
    rock.position.set(random() * 220 - 110, .25, (i % 2 ? 1 : -1) * (7 + random() * 18)); rock.scale.set(1.4, .7, .9); rock.rotation.set(random(), random(), random()); rock.castShadow = true; scene.add(rock);
  }
  // A single merged fern mesh adds detailed foliage without hundreds of draw calls.
  const vertices: number[] = [];
  const colours: number[] = [];
  const triangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, shade: THREE.Color) => {
    for (const point of [a, b, c]) { vertices.push(point.x, point.y, point.z); colours.push(shade.r, shade.g, shade.b); }
  };
  for (let plant = 0; plant < 190; plant++) {
    const origin = new THREE.Vector3(random() * 240 - 120, -.02, (plant % 3 ? -1 : 1) * (6.8 + random() * 15));
    const size = .6 + random() * 1.4;
    for (let frond = 0; frond < 8; frond++) {
      const angle = frond * Math.PI / 4 + random() * .3;
      const forward = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle));
      const side = new THREE.Vector3(-forward.z, 0, forward.x);
      const shade = new THREE.Color().setHSL(.2 + random() * .1, .3 + random() * .2, .11 + random() * .12).convertSRGBToLinear();
      for (let step = 1; step < 11; step++) {
        const t = step / 11;
        const stem = origin.clone().addScaledVector(forward, t * size);
        stem.y += Math.sin(t * Math.PI * .85) * size * .7;
        const width = Math.sin(t * Math.PI) * size * .23;
        for (const direction of [-1, 1]) {
          const tip = stem.clone().addScaledVector(side, width * direction).addScaledVector(forward, size * .17);
          tip.y -= width * .24;
          triangle(stem, tip, stem.clone().addScaledVector(forward, size * .09), shade);
        }
      }
    }
  }
  const fernGeometry = new THREE.BufferGeometry();
  fernGeometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
  fernGeometry.setAttribute("color", new THREE.Float32BufferAttribute(colours, 3));
  fernGeometry.computeVertexNormals();
  const ferns = new THREE.Mesh(fernGeometry, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .95, side: THREE.DoubleSide }));
  ferns.receiveShadow = true;
  scene.add(ferns);
  // Optional scenery: a failed background load must never block runners or race results.
  new THREE.TextureLoader().load("/portal/assets/raptor-racing/jungle-panorama.webp", backdrop => {
    if (!scene.parent && scene.children.length === 0) { backdrop.dispose(); return; }
    backdrop.colorSpace = THREE.SRGBColorSpace; textures.add(backdrop);
    const forestGeometry = new THREE.PlaneGeometry(90, 30);
    const forestMaterial = new THREE.MeshBasicMaterial({ map: backdrop, toneMapped: false, fog: false });
    for (const x of [-90, 0, 90]) {
      const forest = new THREE.Mesh(forestGeometry, forestMaterial);
      forest.position.set(x, 14, -19);
      scene.add(forest);
    }
  }, undefined, () => {});

}

function configureBody(material: THREE.MeshStandardMaterial, pattern: THREE.Texture, parts: THREE.Texture, accent: string, index: number): void {
  const base = new THREE.Color(accent).lerp(new THREE.Color(0x756749), .28);
  const colours = [
    base.clone().multiplyScalar(.78), base.clone().lerp(new THREE.Color(0x92805d), .25), base.clone().multiplyScalar(.2),
    base.clone().lerp(new THREE.Color(0xb6ad86), .42), base.clone().multiplyScalar(.68),
    base.clone().lerp(new THREE.Color(0xd2bd8b), .6), new THREE.Color(0x24130e), new THREE.Color(0xd8c7a1), new THREE.Color(0x4b3827)
  ];
  material.map = pattern;
  material.color.set(0xffffff);
  material.emissive.set(0x000000);
  material.emissiveIntensity = 0;
  material.roughness = 0.64;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, {
      raceParts: { value: parts }, skinRed: { value: colours[0] }, skinGreen: { value: colours[1] }, skinBlue: { value: colours[2] },
      skinCyan: { value: colours[3] }, skinMagenta: { value: colours[4] }, skinYellow: { value: colours[5] },
      partRed: { value: colours[6] }, partGreen: { value: colours[7] }, partBlue: { value: colours[8] }
    });
    shader.fragmentShader = shader.fragmentShader
      .replace("void main() {", "uniform sampler2D raceParts; uniform vec3 skinRed; uniform vec3 skinGreen; uniform vec3 skinBlue; uniform vec3 skinCyan; uniform vec3 skinMagenta; uniform vec3 skinYellow; uniform vec3 partRed; uniform vec3 partGreen; uniform vec3 partBlue; void main() {")
      .replace("#include <map_fragment>", "vec3 m=texture2D(map,vMapUv).rgb; float wr=max(m.r-max(m.g,m.b),0.0),wg=max(m.g-max(m.r,m.b),0.0),wb=max(m.b-max(m.r,m.g),0.0),wc=min(m.g,m.b)*(1.0-m.r),wm=min(m.r,m.b)*(1.0-m.g),wy=min(m.r,m.g)*(1.0-m.b); float sw=max(wr+wg+wb+wc+wm+wy,0.0001); vec3 c=(skinRed*wr+skinGreen*wg+skinBlue*wb+skinCyan*wc+skinMagenta*wm+skinYellow*wy)/sw; if(sw<=0.00011)c=skinGreen; vec3 pm=texture2D(raceParts,vMapUv).rgb; vec3 pw=smoothstep(vec3(0.2),vec3(0.8),vec3(max(pm.r-max(pm.g,pm.b),0.0),max(pm.g-max(pm.r,pm.b),0.0),max(pm.b-max(pm.r,pm.g),0.0))); float ps=pw.r+pw.g+pw.b; if(ps>0.0001)c=mix(c,(partRed*pw.r+partGreen*pw.g+partBlue*pw.b)/ps,clamp(ps,0.0,1.0)); diffuseColor.rgb*=c;");
  };
  material.customProgramCacheKey = () => `raptor-race-${index}`;
  material.needsUpdate = true;
}
